import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AssignedAttributeItems, AssignedGroupItems, AssignedItems, AssignmentAttribute, AssignmentChanges, AssignmentGroup, AssignmentLists, AssignmentPermissions, AssignmentSelection, DashboardAssignmentChangesModel, IAssignmentWithProfiles } from 'src/AngularJS/Dashboard/dashboard-assignment.model';
import { Group as GroupApiModel } from 'src/AngularJS/Models/group.model';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { AgentAttributeValueModel, DashboardResponsiveApiModelSparse, DashboardShareTypeFlags, ProfileApiModel, ProfileShareModel } from 'src/shared/models/dashboard.model';
import { LoginApiModel } from 'src/shared/models/login.model';
import { AgGridHelperService } from '../../services/ag-grid-helper.service';
import { CVLocalizationService } from '../../services/cv-localization.service';
import { AngularAssignToolService } from '../angular-assign-tool.service';
import { AssignToolItemRemovedArgs, AssignToolRightBox } from '../angular-assign-tool/angular-assign-tool.models';
import { DashboardAssignmentPermissionsRendererComponent } from './dashboard-assignment-permissions-renderer/dashboard-assignment-permissions-renderer.component';
import { DashboardAssignmentService } from './dashboard-assignment.service';
import { GridOptions } from 'ag-grid-community';

@Component({
    selector: 'cv-dashboard-assignment',
    templateUrl: './dashboard-assignment.component.html',
    styleUrls: ['./dashboard-assignment.component.scss'],
    providers: [{ provide: AssignToolRightBox, useExisting: forwardRef(() => DashboardAssignmentComponent) }],
})
export class DashboardAssignmentComponent extends AssignToolRightBox implements OnInit, OnChanges {
    /** The item currently being edited inside of the right-box. */
    @Input() activeSelection: AssignmentSelection = null;
    @Output() activeSelectionChange = new EventEmitter<AssignmentSelection>();
    /** List of selections passed from assignList of the assign tool. */
    @Input() dataList: AssignmentSelection[] = null;
    /** Controls whether the other two boxes on the assign tool are disabled or not. */
    @Input() disableContainers = false;
    @Output() disableContainersChange = new EventEmitter<boolean>(true);
    /** Controls whether the apply button on the assign tool is enabled or not. */
    @Input() enableContainerButtons = false;
    @Output() enableContainerButtonsChange = new EventEmitter<boolean>(true);
    /** The dashboards we are configuring the assignments of. */
    @Input() selectedDashboards: DashboardResponsiveApiModelSparse[] = null;
    /** Signal to the assign tool that we are done editing this item. */
    @Output() itemRemoved = new EventEmitter<AssignToolItemRemovedArgs>();
    /** Used to expose the enum to the markup */
    AssignmentPermissions = AssignmentPermissions;
    /** Used to disable everything when true */
    noPermission = false;
    /** Used to decide which warning window is shown */
    showPermissionWarning = AssignmentPermissions.None;
    /** List of permissions that should show a warning */
    warningPermissions = new Set([ AssignmentPermissions.Write, AssignmentPermissions.ReadControl, AssignmentPermissions.Template ]);
    /** Ag-grid settings. */
    gridOptions: GridOptions = null;
    /** Used for the search bar. */
    filterValue = '';
    /** Shows the loader when true. */
    loading = false;
    /** List of roles (profiles in code) in the company */
    availableRoles: ProfileApiModel[];
    /** The assignments organized by their permission used for display in the frontend. */
    displayLists = new Map<AssignmentPermissions, AssignmentLists>();
    /** The assignments as they are set in the tool currently. */
    allAssignments = new AssignmentChanges();
    /** The assignments as they were loaded into the tool. */
    originalAssignments = new AssignmentChanges();
    /** The permission for selection that does not include profiles (Login) as it was loaded into the tool. */
    savedSelectionPermission = AssignmentPermissions.None;
    /** The permission for selection that does not include profiles (Login) as it is currently set in the tool. */
    activeSelectionPermission = AssignmentPermissions.None;
    /** The permissions for each profile for the selection as it was loaded into the tool. */
    savedProfilePermissions = new Map<number, AssignmentPermissions>();
    /** The permissions for each profile for the selection as it is currently set in the tool. */
    activeSelectionProfiles: ProfileShareModel[];

    constructor(
        private agGridHelperService: AgGridHelperService,
        private assignToolService: AngularAssignToolService,
        private dashboardService: DashboardService,
        private localizationService: CVLocalizationService,
        private service: DashboardAssignmentService,
    ) {
        super();
    }

    /**
     * Called when Angular is ready
     */
    async ngOnInit() {
        this.loading = true;
        await this.localizationService.GetSection('Assign_Tool');
        this.setupGrid();

        const readOnly = this.localizationService.getCachedText('Assign_Tool', 'Read_Only', 'Read Only Access');
        const readTheirs = this.localizationService.getCachedText('Assign_Tool', 'Read_Theirs', 'Read with Recipient Access Level');
        const readGroup = this.localizationService.getCachedText('Assign_Tool', 'Read_Group', 'Read with Group Nav');
        const readControl = this.localizationService.getCachedText('Assign_Tool', 'ReadControl', 'Read with Control');
        const writeAccess = this.localizationService.getCachedText('Assign_Tool', 'Write_Access', 'Write Access');
        const writeTheirs = this.localizationService.getCachedText('Assign_Tool', 'Write_Theirs', 'Write with Recipient Access Level');
        const templateAccess = this.localizationService.getCachedText('Assign_Tool', 'Template_Access', 'Template');

        this.displayLists.set(AssignmentPermissions.Read, new AssignmentLists(readOnly, AssignmentPermissions.Read));
        this.displayLists.set(AssignmentPermissions.ReadTheirs, new AssignmentLists(readTheirs, AssignmentPermissions.ReadTheirs));
        this.displayLists.set(AssignmentPermissions.ReadGroup, new AssignmentLists(readGroup, AssignmentPermissions.ReadGroup));
        this.displayLists.set(AssignmentPermissions.ReadControl, new AssignmentLists(readControl, AssignmentPermissions.ReadControl));
        this.displayLists.set(AssignmentPermissions.Write, new AssignmentLists(writeAccess, AssignmentPermissions.Write));
        this.displayLists.set(AssignmentPermissions.WriteTheirs, new AssignmentLists(writeTheirs, AssignmentPermissions.WriteTheirs));
        this.displayLists.set(AssignmentPermissions.Template, new AssignmentLists(templateAccess, AssignmentPermissions.Template));

        this.noPermission = this.selectedDashboards?.some(dashboard => dashboard.SharedBy && !dashboard.SharedWriteAccess) ?? false;

        this.availableRoles = await this.assignToolService.loadRoles();

        if (this.selectedDashboards?.length === 1) {
            const dashboard = this.selectedDashboards[0];


            dashboard.RecipientLogins?.forEach(login => {
                const permission = this.getAssignmentPermissionFromFlags(login);

                this.allAssignments.logins.set(login.LoginID, { login, permission });
                this.originalAssignments.logins.set(login.LoginID, { login, permission });
                this.dataList.push({...login});
            });

            Object.values(dashboard.RecipientGroups ?? {}).forEach(({ Group, Profiles }) => {
                this.allAssignments.groups.set(Group.GroupID, new AssignmentGroup(Group));
                this.originalAssignments.groups.set(Group.GroupID, new AssignmentGroup(Group));

                Profiles.forEach(profile => {
                    const permission = this.getAssignmentPermissionFromFlags(profile);
                    this.allAssignments.groups.get(Group.GroupID).profiles.set(profile.ProfileID, { profile, permission });
                    this.originalAssignments.groups.get(Group.GroupID).profiles.set(profile.ProfileID, { profile, permission });
                });

                this.dataList.push({...Group});
            });

            Object.values(dashboard.RecipientAttributes ?? {}).forEach(({ Attribute, Profiles }) => {
                const id = Attribute.AttributeValueID;
                this.allAssignments.attributes.set(id, new AssignmentAttribute(Attribute));
                this.originalAssignments.attributes.set(id, new AssignmentAttribute(Attribute));

                Profiles.forEach(profile => {
                    const permission = this.getAssignmentPermissionFromFlags(profile);
                    this.allAssignments.attributes.get(id).profiles.set(profile.ProfileID, { profile, permission });
                    this.originalAssignments.attributes.get(id).profiles.set(profile.ProfileID, { profile, permission });
                });

                this.dataList.push({...Attribute});
            });
        }

        this.updateDisplayLists();
        this.loading = false;
    }

    /**
     * Called when changes are detected.
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes.activeSelection?.currentValue != null) {
            this.onSelectionChange();
        }
    }

    /**
     * Sets up the ag-grid with the options we want.
     */
    setupGrid() {
        const selectionChangeEventEmitter = new EventEmitter<AssignmentPermissions>();
        selectionChangeEventEmitter.subscribe(val => this.showWarningForActivePermission(val));

        this.gridOptions = this.agGridHelperService.createGridOptions({
            components: {
                permissionRenderer: DashboardAssignmentPermissionsRendererComponent,
            },
            columnDefs: [
                {
                    field: 'ProfileName',
                    headerName: this.localizationService.getCachedText('Assign_Tool', 'Roles', 'Roles'),
                    suppressMenu: true,
                    cellStyle: {
                        display: 'flex',
                        'justify-content': 'center',
                        'flex-flow': 'column'
                    }
                },
                {
                    field: "Permissions",
                    cellClass: 'd-flex flex-column justify-content-center',
                    cellRenderer: 'permissionRenderer',
                    cellRendererParams: {
                        permissionChanged: selectionChangeEventEmitter,
                    },
                    headerName: this.localizationService.getCachedText('Assign_Tool', 'Permissions', 'Permissions'),
                    suppressMenu: true,
                    minWidth: 180,
                }
            ],
            defaultColDef: {
                sortable: false,
                filter: true
            },

            getRowId: ({data: rowData}) => rowData.ProfileID,
            suppressContextMenu: true,
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            suppressCopyRowsToClipboard: true,
            rowHeight: 35,
            popupParent: document.body,
            alwaysShowVerticalScroll: true,
            enableRangeSelection: false,

            isExternalFilterPresent: () => true,
            doesExternalFilterPass: node => node.data.ProfileName?.toLowerCase().includes(this.filterValue.toLowerCase()),
            onGridReady: (e) => {
                e.api.sizeColumnsToFit();
            },
            onGridSizeChanged: (e) => e.api.sizeColumnsToFit(),
        });
    }

    /**
     * Determines whether the {@link AssignmentSelection} is a Login based on LoginID existing.
     */
    selectedIsLogin(s: AssignmentSelection): s is LoginApiModel {
        return 'LoginID' in s;
    }

    /**
     * Determines whether the {@link AssignmentSelection} is a Group based on GroupID and GroupLevel existing.
     */
    selectedIsGroup(s: AssignmentSelection): s is GroupApiModel  {
        return 'GroupID' in s && 'GroupLevel' in s;
    }

    /**
     * Determines whether the {@link AssignmentSelection} is an AgentAttributeValue based on AttributeValueID existing.
     */
    selectedIsAgentAttribute(s: AssignmentSelection): s is AgentAttributeValueModel  {
        return 'AttributeValueID' in s;
    }

    /**
     * Called when the item being edited changes.
     */
    onSelectionChange() {
        if (this.noPermission)
            return;

        this.disableContainersChange.emit(true);
        this.enableContainerButtonsChange.emit(false);
        this.savedProfilePermissions = new Map<number, AssignmentPermissions>();

        if (this.selectedIsLogin(this.activeSelection)) {
            const loginAssignments = this.allAssignments.logins.get(this.activeSelection.LoginID);
            this.activeSelectionPermission = loginAssignments?.permission ?? AssignmentPermissions.None;
            this.savedSelectionPermission = this.activeSelectionPermission;
        } else if (this.selectedIsGroup(this.activeSelection)) {
            const groupAssignments = this.allAssignments.groups.get(this.activeSelection.GroupID);
            this.activeSelectionProfiles = this.availableRoles.map(p => {
                const permission = groupAssignments?.profiles.get(p.ProfileID)?.permission ?? AssignmentPermissions.None;
                this.savedProfilePermissions.set(p.ProfileID, permission);
                return {...p, Permissions: permission };
            });
        } else if (this.selectedIsAgentAttribute(this.activeSelection)) {
            const attributeAssignments = this.allAssignments.attributes.get(this.activeSelection.AttributeValueID);
            this.activeSelectionProfiles = this.availableRoles.map(p => {
                const permission = attributeAssignments?.profiles.get(p.ProfileID)?.permission ?? AssignmentPermissions.None;
                this.savedProfilePermissions.set(p.ProfileID, permission);
                return { ...p, Permissions: permission };
            });
        }
    }

    /**
     * Enable the warning message if needed for this permission.
     */
    showWarningForActivePermission(permission: AssignmentPermissions = this.activeSelectionPermission) {
        if (this.warningPermissions.has(permission) && !this.service.acknowledgedWarnings.has(permission)) {
            this.showPermissionWarning = permission;
        }
    }

    /**
     * Marks the warning as acknowledged and hides the message.
     */
    acceptWarningForPermission(permission: AssignmentPermissions) {
        this.service.acknowledgedWarnings.add(permission);
        this.showPermissionWarning = AssignmentPermissions.None;
    }

    /**
     * Updates items in allAssignments based on the options selected for the actively edited item.
     */
    save() {
        if (this.selectedIsLogin(this.activeSelection)) {
            const loginId = this.activeSelection.LoginID;
            if (this.activeSelectionPermission !== this.savedSelectionPermission) {
                if (this.activeSelectionPermission === AssignmentPermissions.None)
                    this.allAssignments.logins.delete(loginId);
                else {
                    this.allAssignments.logins.set(loginId, {
                        login: this.activeSelection,
                        permission: this.activeSelectionPermission
                    });
                }
            }
        } else if (this.selectedIsGroup(this.activeSelection)) {
            const groupId = this.activeSelection.GroupID;
            if (!this.allAssignments.groups.has(groupId))
                this.allAssignments.groups.set(groupId, new AssignmentGroup(this.activeSelection));

            const groupAssignments = this.allAssignments.groups.get(groupId);
            this.updateAssignmentsFromSelection(groupAssignments);
            if (groupAssignments.profiles.size === 0)
                this.allAssignments.groups.delete(groupId);
        } else if (this.selectedIsAgentAttribute(this.activeSelection)) {
            const attributeId = this.activeSelection.AttributeValueID;
            if (!this.allAssignments.attributes.has(attributeId))
                this.allAssignments.attributes.set(attributeId, new AssignmentAttribute(this.activeSelection));

            const attrAssignments = this.allAssignments.attributes.get(attributeId);
            this.updateAssignmentsFromSelection(attrAssignments);
            if (attrAssignments.profiles.size === 0)
                this.allAssignments.attributes.delete(attributeId);
        }

        this.updateDisplayLists();
        this.deselect();
    }

    /**
     * Saves the assignment changes to the backend.
     * Called from parent component via ViewChild.
     */
    async apply() {
        const changes = this.getAssignmentChanges();
        if (changes.length === 0)
            return;

        this.loading = true;
        this.enableContainerButtonsChange.emit(false);

        try {
            const selectedKeys = this.selectedDashboards.map(d => d.DashboardKey);
            await this.service.saveDashboardAssignments(selectedKeys, changes);
            this.dashboardService.reloadDashboards.next([selectedKeys, changes]);
        } catch (err) {
            console.error("Error while saving assignments", err);
        } finally {
            this.loading = false;
            this.enableContainerButtonsChange.emit(true);
        }
    }

    /**
     * Changes the activeSelection and notifies the parent that we are changing the activeSelection.
     */
    edit(selection: AssignmentSelection) {
        this.activeSelectionChange.emit(selection);
    }

    /**
     * Removes a permission from allAssignments from either a login or a group/attribute and the roles that were assigned the permission.
     *
     * @param selected The item being removed from allAssignments.
     * @param permission The permission to remove from the item. Does not apply to login as the entire login is removed.
     */
    remove(selected: AssignmentSelection, permission: AssignmentPermissions) {
        if (this.selectedIsLogin(selected)) {
            this.allAssignments.logins.delete(selected.LoginID);
        } else if (this.selectedIsGroup(selected)) {
            const groupAssignments = this.allAssignments.groups.get(selected.GroupID);
            this.removePermissionFromAssignments(groupAssignments, permission);
            if (groupAssignments.profiles.size === 0)
                this.allAssignments.groups.delete(selected.GroupID);
        } else if (this.selectedIsAgentAttribute(selected)) {
            const attrAssignments = this.allAssignments.attributes.get(selected.AttributeValueID);
            this.removePermissionFromAssignments(attrAssignments, permission);
            if (attrAssignments.profiles.size === 0)
                this.allAssignments.attributes.delete(selected.AttributeValueID);
        }

        this.updateDisplayLists();
    }

    /**
     * Closes the edit view of an item and enables the parent container to select another.
     */
    deselect() {
        if (this.selectedIsLogin(this.activeSelection)) {
            if (!this.allAssignments.logins.has(this.activeSelection.LoginID)) {
                for (let i = this.dataList.length - 1; i >= 0; i--) {
                    const login = this.dataList[i];
                    if (this.selectedIsLogin(login) && login.LoginID === this.activeSelection.LoginID) {
                        this.dataList.splice(i, 1);
                        this.itemRemoved.emit({ dto: null, login: this.activeSelection });
                    }
                }
            }
        } else if (this.selectedIsGroup(this.activeSelection)) {
            if (!this.allAssignments.groups.has(this.activeSelection.GroupID)) {
                for (let i = this.dataList.length - 1; i >= 0; i--) {
                    const group = this.dataList[i];
                    if (this.selectedIsGroup(group) && group.GroupID === this.activeSelection.GroupID) {
                        this.dataList.splice(i, 1);
                        this.itemRemoved.emit({ dto: this.activeSelection });
                    }
                }
            }
        } else if (this.selectedIsAgentAttribute(this.activeSelection)) {
            if (!this.allAssignments.attributes.has(this.activeSelection.AttributeValueID)) {
                for (let i = this.dataList.length - 1; i >= 0; i--) {
                    const attribute = this.dataList[i];
                    if (this.selectedIsAgentAttribute(attribute) && attribute.AttributeValueID === this.activeSelection.AttributeValueID) {
                        this.dataList.splice(i, 1);
                        this.itemRemoved.emit({ dto: this.activeSelection });
                    }
                }
            }
        }

        this.activeSelection = null;
        this.activeSelectionChange.emit(null);
        this.enableContainerButtonsChange.emit(true);
        this.disableContainersChange.emit(false);
    }

    /**
     * Translates the flags from the models into the enum we used in this tool.
     */
    private getAssignmentPermissionFromFlags(flags: DashboardShareTypeFlags) {
        if (flags.template)
            return AssignmentPermissions.Template;
        if (flags.write)
            return AssignmentPermissions.Write;
        if (flags.readControl)
            return AssignmentPermissions.ReadControl;
        if (flags.readGroup)
            return AssignmentPermissions.ReadGroup;
        if (flags.read)
            return AssignmentPermissions.Read;
        if (flags.writeTheirs)
            return AssignmentPermissions.WriteTheirs;
        if (flags.readTheirs)
            return AssignmentPermissions.ReadTheirs;

        return AssignmentPermissions.None;
    }

    /**
     * Populates the list used in the markup to display any changes made in allAssignments.
     */
    private updateDisplayLists() {
        this.displayLists.forEach(v => v.items = new AssignedItems());

        this.allAssignments.logins.forEach(login => {
            if (login.permission === AssignmentPermissions.None)
                return;
            this.displayLists.get(login.permission).items.logins.push(login.login);
        });

        this.allAssignments.groups.forEach(({ group, profiles }) => {
            profiles.forEach(({ permission, profile }) => {
                if (permission === AssignmentPermissions.None)
                    return;

                const assignmentList = this.displayLists.get(permission);
                const groupsList = assignmentList.items.groups;
                if (!groupsList.has(group.GroupID))
                    groupsList.set(group.GroupID, new AssignedGroupItems(group));
                groupsList.get(group.GroupID).roles.push(profile);
            });
        });

        this.allAssignments.attributes.forEach(({ attribute, profiles }) => {
            profiles.forEach(({ permission, profile }) => {
                if (permission === AssignmentPermissions.None)
                    return;

                const assignmentList = this.displayLists.get(permission);
                const attributeList = assignmentList.items.attributes;
                if (!attributeList.has(attribute.AttributeValueID))
                    attributeList.set(attribute.AttributeValueID, new AssignedAttributeItems(attribute));
                attributeList.get(attribute.AttributeValueID).roles.push(profile);
            });
        });
    }

    /**
     * Generates a changes list of permissions that were changed from the loaded originalAssignments to the currently edited allAssignments.
     */
    private getAssignmentChanges() {
        const changes: DashboardAssignmentChangesModel[] = [];

        // first add all additions or changes
        this.allAssignments.logins.forEach(({ permission }, loginId) => {
            const originalAssignment = this.originalAssignments.logins.get(loginId);
            if (originalAssignment?.permission !== permission) {
                changes.push({
                    isLogin: true,
                    id: loginId,
                    permission,
                });
            }
        });

        this.allAssignments.groups.forEach(({ group, profiles }, groupId) => {
            const originalGroups = this.originalAssignments.groups.get(groupId);
            profiles.forEach(({ permission }, profileId) => {
                if (originalGroups?.profiles.get(profileId)?.permission !== permission) {
                    changes.push({
                        isLogin: false,
                        id: profileId,
                        permission,
                        companyID: group.CompanyID,
                        groupID: group.GroupID,
                        groupLevel: group.GroupLevel,
                        baseGroupType: group.BaseGroupType,
                    });
                }
            });
        });

        this.allAssignments.attributes.forEach(({ attribute, profiles }, attrId) => {
            const originalAttrs = this.originalAssignments.groups.get(attrId);
            profiles.forEach(({ permission }, profileId) => {
                if (originalAttrs?.profiles.get(profileId)?.permission !== permission) {
                    changes.push({
                        isLogin: false,
                        id: profileId,
                        permission,
                        companyID: attribute.CompanyID,
                        groupID: 0,
                        groupLevel: 0,
                        baseGroupType: 2,
                        userAttributeID: attribute.AttributeValueID,
                    });
                }
            });
        });

        // add a change to "None" for anything that is removed
        this.originalAssignments.logins.forEach((_, loginId) => {
            if (!this.allAssignments.logins.has(loginId)) {
                changes.push({
                    isLogin: true,
                    id: loginId,
                    permission: AssignmentPermissions.None,
                });
            }
        });

        this.originalAssignments.groups.forEach(({ group, profiles }, groupId) => {
            const groupAssignments = this.allAssignments.groups.get(groupId);
            profiles.forEach((_, profileId) => {
                if (!groupAssignments?.profiles.has(profileId)) {
                    changes.push({
                        isLogin: false,
                        id: profileId,
                        permission: AssignmentPermissions.None,
                        companyID: group.CompanyID,
                        groupID: group.GroupID,
                        groupLevel: group.GroupLevel,
                        baseGroupType: group.BaseGroupType,
                    });
                }
            });
        });

        this.originalAssignments.attributes.forEach(({ attribute, profiles }, attrId) => {
            const attrAssignments = this.allAssignments.attributes.get(attrId);
            profiles.forEach((_, profileId) => {
                if (!attrAssignments?.profiles.has(profileId)) {
                    changes.push({
                        isLogin: false,
                        id: profileId,
                        permission: AssignmentPermissions.None,
                        companyID: attribute.CompanyID,
                        groupID: 0,
                        groupLevel: 0,
                        baseGroupType: 2,
                        userAttributeID: attribute.AttributeValueID,
                    });
                }
            });
        });

        return changes;
    }

    /**
     * Updates the role (profile) permissions in the assignments list against what is selected in the editor.
     */
    private updateAssignmentsFromSelection(assignments: IAssignmentWithProfiles) {
        this.activeSelectionProfiles.forEach(profile => {
            const permission = profile.Permissions;
            if (profile.Permissions === this.savedProfilePermissions.get(profile.ProfileID))
                return;

            if (profile.Permissions === AssignmentPermissions.None)
                assignments.profiles.delete(profile.ProfileID);
            else
                assignments.profiles.set(profile.ProfileID, { profile, permission});
        });
    }

    /**
     * Removes any role (profile) permissions from the assignments list that have the permission passed in.
     */
    private removePermissionFromAssignments(assignments: IAssignmentWithProfiles, permission: AssignmentPermissions) {
        assignments.profiles.forEach((profile, profileId) => {
            if (profile.permission !== permission)
                return;
            assignments.profiles.delete(profileId);
        });
    }
}
